import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import LOCAL_STORAGE_KEYS from '@rover/constants/src/search/localStorage.constants';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import { SERVICE_SELECTION_OPTIONS } from '@rover/constants/src/translations/serviceSelector.constants';
import { Box, Button, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { ServiceSelectorV2 } from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/components/ServiceSelector';
import { GroomingHomepageSearchClick } from '@rover/react-lib/src/pages/grooming/analyticsEvents';
import NewServiceBadge from '@rover/react-lib/src/pages/grooming/components/NewServiceBadge';
import { GROOMING_SEARCH_URL } from '@rover/react-lib/src/pages/grooming/constants';
import useInGroomingPilot from '@rover/react-lib/src/pages/grooming/hooks/useInGroomingPilot';
import emitMetricOnCtaClick from '@rover/react-lib/src/pages/home/emitMetricOnCtaClick';
import HomePageSearchBoxLocationInput from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/connectors/LocationInputConnector';
import SearchBoxHiddenInputs from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/SearchBoxHiddenInputs';
import type { UpdateFiltersFunc } from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/UpdateFiltersFunc';
import useIsDefaultLocale from '@rover/react-lib/src/pages/index/HomePage/hooks/useIsDefaultLocale';
import buildSearchEvent from '@rover/react-lib/src/pages/index/HomePage/utilities/buildSearchEvent';
import useInTrainingPilot from '@rover/react-lib/src/pages/training/hooks/useInTrainingPilot';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useFlag from '@rover/shared/js/hooks/useFlag';
import {
  MobileWebHomePageServiceTypeChange,
  MobileWebHomePageServiceTypeDropdownClick,
} from '@rover/shared/js/search/utilities/analyticsEvents';
import getBrandSpecificValue from '@rover/shared/js/utils/getBrandSpecificValue';
import { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import type { Pet } from '@rover/types/src/Pet';
import type { ServiceType, ServiceTypeOption } from '@rover/types/src/ServiceType';
import fireCrossPageEvent from '@rover/utilities/fireCrossPageEvent';

export interface NarrowDeviceSearchBoxProps {
  isAuthenticated: boolean;
  pets: Pet[];
  filters: HomepageSearchFilters;
  serviceType: ServiceType | undefined;
  serviceTypeOptions: ServiceTypeOption[];
  updateFilters: UpdateFiltersFunc;
  currentUserPostalCode?: string;
}

const ctaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Search`,
});

const getStartedCtaText = getBrandSpecificValue<MessageDescriptor>({
  rover: msg`Get Started`,
});

const NarrowDeviceSearchBox: FC<NarrowDeviceSearchBoxProps> = ({
  isAuthenticated,
  serviceType,
  serviceTypeOptions,
  filters,
  updateFilters,
}) => {
  const { i18n } = useLingui();
  // Sticky interaction flag: once the user touches any field it stays true for
  // the component's lifetime, even if the field is later cleared.
  const [hasInteracted, setHasInteracted] = useState(false);
  const markInteraction = useCallback(() => setHasInteracted(true), []);

  const isInGeoBrowseExperiment = useFlag(FLAGS.GEO_BROWSE_HOMEPAGE_ENTRYPOINT);
  // The geo-browse entrypoint is only translated for each country's default
  // locale, so restrict it to that locale and fall back to Search otherwise.
  const isDefaultLocale = useIsDefaultLocale();
  const { radii, text, space, textColor } = useTheme();
  const [isDogTraining, setIsDogTraining] = useState(false);

  const { shouldShowTrainingEntrypoints } = useInTrainingPilot();
  const { shouldShowGroomingEntrypoints } = useInGroomingPilot();

  const { setScreenReaderAnnouncement } = useA11yContext();

  const isGrooming =
    filters.serviceTypeSlug === SERVICE_TYPE_CHOICES.grooming && shouldShowGroomingEntrypoints;

  useEffect(() => {
    setIsDogTraining(
      serviceType?.slug === SERVICE_TYPE_CHOICES.training && shouldShowTrainingEntrypoints
    );
  }, [serviceType?.slug, shouldShowTrainingEntrypoints]);

  const groomingSearchUrl = useMemo((): string => {
    if (!isGrooming) return '';
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.centerlat) params.set('centerlat', filters.centerlat);
    if (filters.centerlng) params.set('centerlng', filters.centerlng);
    if (filters.locationType) params.set('locationType', filters.locationType);
    const paramString = params.toString();
    return paramString ? `${GROOMING_SEARCH_URL}?${paramString}` : GROOMING_SEARCH_URL;
  }, [isGrooming, filters.location, filters.centerlat, filters.centerlng, filters.locationType]);

  const serviceTypes = useMemo((): ServiceTypeOption[] => {
    const hasMatchingServiceType = (option): boolean =>
      serviceTypeOptions.some((service) => service.value === option.value);

    return SERVICE_SELECTION_OPTIONS()
      .filter((option) => {
        if (option.value === SERVICE_TYPE_CHOICES.grooming) {
          return shouldShowGroomingEntrypoints;
        }
        if (option.value === SERVICE_TYPE_CHOICES.training) {
          return shouldShowTrainingEntrypoints && hasMatchingServiceType(option);
        }
        return hasMatchingServiceType(option);
      })
      .map((option) =>
        option.value === SERVICE_TYPE_CHOICES.grooming
          ? { ...option, labelBadge: NewServiceBadge }
          : option
      );
  }, [serviceTypeOptions, shouldShowTrainingEntrypoints, shouldShowGroomingEntrypoints]);

  // Route unauthenticated, not-yet-interacted users to the /get-started/
  // interstitial when the geo browse gate is enabled.
  const action = (() => {
    if (isDogTraining) return '/training/search/';
    if (isInGeoBrowseExperiment && !isAuthenticated && !hasInteracted && isDefaultLocale) {
      return '/get-started/';
    }
    return '/search/';
  })();
  const routesToGetStarted = action === '/get-started/';

  // Native GET form submission: do NOT preventDefault — let the browser navigate.
  const handleSubmit = useCallback(() => {
    setScreenReaderAnnouncement(t`Form submitted. Navigating to Search page.`);
    fireCrossPageEvent(LOCAL_STORAGE_KEYS.HOMEPAGE_SEARCH_EVENT, buildSearchEvent(filters));

    if (isDogTraining) {
      emitMetricOnCtaClick('training-homepage-cta-click');
    }
  }, [filters, isDogTraining, setScreenReaderAnnouncement]);

  return (
    <Flex
      data-testid="narrow-device-search-box"
      flexDirection="column"
      background="primary"
      paddingY="6x"
      paddingX="4x"
      marginX="4x"
      variant="elevation_low"
      borderRadius={radii.primary}
    >
      {serviceTypes.length > 0 && (
        <Box mb="6x">
          <ServiceSelectorV2
            label={t`Which service do you need?`}
            inputFontSize={text['200'].fontSize}
            serviceTypeOptions={serviceTypes}
            serviceType={filters.serviceTypeSlug}
            onOpen={() => {
              markInteraction();
              emitAnalyticsEvent(
                new MobileWebHomePageServiceTypeDropdownClick({
                  serviceType: filters.serviceTypeSlug,
                })
              );
            }}
            onChange={(serviceTypeSlug) => {
              markInteraction();
              if (filters.serviceTypeSlug !== serviceTypeSlug) {
                emitAnalyticsEvent(
                  new MobileWebHomePageServiceTypeChange({
                    serviceType: serviceTypeSlug,
                  })
                );
              }
              updateFilters({ serviceTypeSlug });
            }}
          />
        </Box>
      )}
      <Box mb="6x">
        <HomePageSearchBoxLocationInput
          updateFilters={updateFilters}
          onInteract={markInteraction}
          serviceType={serviceType}
          inputHeight={space['12x']}
          inputFontSize={text['200'].fontSize}
          placeholder={t`Add your address`}
          label={t`What's your address?`}
          labelMb={space['1x']}
          labelColor={textColor.primary}
          {...(filters.location ? { inputValue: filters.location } : {})}
        />
      </Box>
      {isGrooming ? (
        <Button
          data-testid="search-box-grooming-redirect"
          size="small"
          fullWidth
          variant="primary"
          href={groomingSearchUrl}
          onPress={() => {
            emitAnalyticsEvent(new GroomingHomepageSearchClick());
            emitMetricOnCtaClick('grooming-homepage-cta-click');
          }}
        >
          {i18n._(ctaText)}
        </Button>
      ) : (
        <form method="GET" action={action} onSubmit={handleSubmit}>
          <SearchBoxHiddenInputs
            filters={filters}
            bypassInterstitial
            isDogTraining={isDogTraining}
          />
          <Button
            data-testid="search-box-submit"
            size="small"
            fullWidth
            variant="primary"
            webProps={{ type: 'submit' }}
          >
            {i18n._(routesToGetStarted ? getStartedCtaText : ctaText)}
          </Button>
        </form>
      )}
    </Flex>
  );
};

export default NarrowDeviceSearchBox;
